// Mirrors the API's valuation-state contract (api/src/Api/Domain/Valuation/entities.ts) exhaustively, so an unrecognized response is a type error rather than a number rendered as though it were complete.

// Leaf imports, not the `utils` barrel: that barrel re-exports `accountTransformers`, which imports from `services`, closing a cycle back onto this file.
import { BadgeVariant } from "../components/Badge/types";
import { formatCurrency, formatSignedPercent } from "../utils/numbers";
import { formatDate } from "../utils/strings";
import { DateFormat } from "../utils/types";

export type ValuationState =
  "AVAILABLE" | "PENDING" | "UNAVAILABLE" | "MANUAL_ACTION_REQUIRED";

export type ValuationStateReason =
  // PENDING
  | "HISTORY_IMPORT_PENDING"
  | "CURRENT_QUOTE_FETCH_PENDING"
  // UNAVAILABLE
  | "HISTORY_COVERAGE_UNAVAILABLE"
  | "PRE_2020_OVERRIDE_REQUIRED"
  | "CURRENT_QUOTE_UNAVAILABLE"
  | "FX_RATE_UNAVAILABLE"
  | "PRICE_BASIS_UNVERIFIED"
  | "EXCHANGE_TIMEZONE_UNAVAILABLE"
  | "LEGACY_VALUATION_UNVERIFIED"
  // MANUAL_ACTION_REQUIRED
  | "UNSUPPORTED_CORPORATE_ACTION"
  | "REVERSE_SPLIT_CASH_IN_LIEU";

export interface ValuationStatus {
  state: ValuationState;
  reason: ValuationStateReason | null;
  asOf: string | null;
}

/** Sits beside `ValuationStatus` rather than folded into it: a stale FX rate is worth surfacing but must never turn an available value into an unavailable one. */
export interface ValuationWarning {
  code: "STALE_FX_RATE";
  instrumentId: number;
  quoteDate: string;
  fxAsOfDate: string;
}

// Nullish-tolerant on purpose: a figure with no status attached is still a figure, and every other gate in this file routes through here so availability is never decided twice.
export const isAvailable = (valuation?: ValuationStatus | null): boolean =>
  !valuation || valuation.state === "AVAILABLE";

/** Badge text, severity and the sentence that serves as both note and accessible name. */
export interface ValuationBadge {
  label: string;
  variant: BadgeVariant;
  message: string;
}

/** Which price sources valued a history point; a mixed point lists each one. */
export type PriceProvenanceKind = "provider" | "sharedManual" | "userOverride";

/** Keys are required-but-nullable, not optional: an all-optional shape would let a response that dropped a field keep compiling here and silently stop rendering the line. */
export interface PriceProvenance {
  providerMarketDate: string | null;
  quoteFetchedAt: string | null;
}

/** Release B (PMA-359): presence, never `??` — a canonical `null` that is present is the answer, and a stale alias must not win it. Legacy keys are tried in the order given, the first non-null one answering. */
export const canonicalOr = <
  Row extends object,
  C extends keyof Row & string,
  L extends keyof Row & string,
>(
  row: Row,
  canonical: C,
  ...legacy: L[]
): NonNullable<Row[C | L]> | null => {
  const value =
    canonical in row
      ? row[canonical]
      : legacy.reduce<Row[L] | undefined>(
          (found, key) => found ?? row[key],
          undefined,
        );
  return (value ?? null) as NonNullable<Row[C | L]> | null;
};

/** A history point's valuation as the wire states it: the canonical flat trio, with the Release-B nested form beside it. */
export interface HistoryPointValuation {
  valuationState?: ValuationState;
  valuationStateReason?: ValuationStateReason | null;
  valuationAsOf?: string | null;
  valuation?: ValuationStatus | null;
}

// `valuationState` is never null on the wire, so its presence alone picks the canonical branch; no status
// at all is a figure, not an absence (`isAvailable`'s rule).
export const historyPointValuationOf = (
  point: HistoryPointValuation,
): ValuationStatus => {
  const state = point.valuationState;
  if (state !== undefined) {
    return {
      state,
      reason: point.valuationStateReason ?? null,
      asOf: point.valuationAsOf ?? null,
    };
  }
  return point.valuation ?? { state: "AVAILABLE", reason: null, asOf: null };
};

export interface PriceUpdated {
  text: string;
  /** Optional market-close context, kept secondary to PMA's visible refresh timestamp. */
  title?: string;
}

export interface ValuationAmount {
  value: number | null;
  valuation?: ValuationStatus | null;
  /** Defaults to `formatCurrency`; pass a surface's own formatter so adopting the state rule doesn't restyle its numbers. */
  format?: (value: number) => string;
}

// One constant so a value and its explanatory note can never disagree about whether there is a figure at all.
export const VALUATION_PLACEHOLDER = "Unavailable";

// A dash, not "Unavailable": a value still on its way is not the same absence as one that isn't coming.
export const VALUATION_PENDING_PLACEHOLDER = "—";

// One sentence per state: PENDING/MANUAL_ACTION_REQUIRED/UNAVAILABLE have different recovery paths, so a shared message would misdirect the user.
const VALUATION_MESSAGES: Record<ValuationState, string> = {
  AVAILABLE: "Current valuation unavailable",
  PENDING: "Awaiting current quote",
  UNAVAILABLE: "Current valuation unavailable",
  MANUAL_ACTION_REQUIRED: "Needs a correction before it can be valued",
};

// One sentence per reason — a state alone can't say what's wrong — checked before the state map, which stays the fallback for a reason the API hasn't sent yet. Every reason in the union is spelled out so an unfamiliar one renders honestly instead of falling back.
const VALUATION_REASON_MESSAGES: Partial<Record<ValuationStateReason, string>> =
  {
    HISTORY_IMPORT_PENDING: "Importing this instrument's price history",
    CURRENT_QUOTE_FETCH_PENDING: "Awaiting current quote",
    HISTORY_COVERAGE_UNAVAILABLE: "No market price available for this period",
    // Also used as a label/accessible name on several surfaces (hero headline, account card, goal card, confirm modal) — keep it one clause; the fuller form lives in VALUATION_REASON_DETAILS.
    PRE_2020_OVERRIDE_REQUIRED: "Add your own price to value this period",
    CURRENT_QUOTE_UNAVAILABLE: "No current quote for this instrument",
    FX_RATE_UNAVAILABLE: "No exchange rate to convert this price to euros",
    PRICE_BASIS_UNVERIFIED: "This instrument's price basis is unverified",
    EXCHANGE_TIMEZONE_UNAVAILABLE:
      "This exchange's trading calendar is unknown",
    LEGACY_VALUATION_UNVERIFIED: "This older valuation has not been verified",
    UNSUPPORTED_CORPORATE_ACTION:
      "A corporate action here needs a correction first",
    REVERSE_SPLIT_CASH_IN_LIEU:
      "Record the cash from this reverse split to value it",
  };

// Absent/unknown valuations get the generic copy: no state is not evidence that anything is on its way.
export const valuationMessageFor = (
  valuation?: ValuationStatus | null,
): string => {
  if (!valuation) {
    return VALUATION_MESSAGES.UNAVAILABLE;
  }
  const reasonMessage = valuation.reason
    ? VALUATION_REASON_MESSAGES[valuation.reason]
    : undefined;
  return (
    reasonMessage ??
    VALUATION_MESSAGES[valuation.state] ??
    VALUATION_MESSAGES.UNAVAILABLE
  );
};

// Longer form for the two surfaces with room to explain the policy, not just name the remedy. Opt-in per reason; unlisted reasons fall back to the short `valuationMessageFor` copy.
const VALUATION_REASON_DETAILS: Partial<Record<ValuationStateReason, string>> =
  {
    PRE_2020_OVERRIDE_REQUIRED:
      "Before 2020, only your private price can value this period; otherwise it remains unavailable.",
  };

/** For a wrapping-sentence surface only — never a label, badge name, or composed string; those stay on `valuationMessageFor`. */
export const valuationDetailFor = (
  valuation?: ValuationStatus | null,
): string => {
  const detail = valuation?.reason
    ? VALUATION_REASON_DETAILS[valuation.reason]
    : undefined;
  return detail ?? valuationMessageFor(valuation);
};

// Keyed like the state/reason maps so an unhandled code is a type error, not a borrowed FX sentence.
const VALUATION_WARNING_MESSAGES: Record<
  ValuationWarning["code"],
  (warning: ValuationWarning) => string
> = {
  STALE_FX_RATE: (warning) =>
    `FX rate from ${formatDate(warning.fxAsOfDate, DateFormat.DMY_SPACE)} used for the ${formatDate(warning.quoteDate, DateFormat.DMY_SPACE)} quote`,
};

/** The one visible explanation for a non-blocking valuation warning. */
export const valuationWarningText = (warning: ValuationWarning): string =>
  VALUATION_WARNING_MESSAGES[warning.code](warning);

// Separate from VALUATION_BADGE_LABELS because it's keyed on codes, not states: a warning never changes the value, only what's worth saying about it.
const VALUATION_WARNING_BADGE_LABELS: Record<ValuationWarning["code"], string> =
  {
    STALE_FX_RATE: "Stale FX",
  };

const VALUATION_WARNING_LEADS: Record<ValuationWarning["code"], string> = {
  STALE_FX_RATE: "Stale exchange rate",
};

/** The short headline a warning strip leads with, ahead of `valuationWarningText`. */
export const valuationWarningLeadFor = (warning: ValuationWarning): string =>
  VALUATION_WARNING_LEADS[warning.code];

/** Always `warning` severity: by definition these never block a value and aren't owner-actionable. */
export const valuationWarningBadgeFor = (
  warning: ValuationWarning,
): ValuationBadge => ({
  label: VALUATION_WARNING_BADGE_LABELS[warning.code],
  variant: "warning",
  message: valuationWarningText(warning),
});

// Deliberately not the full sentence: the badge sits inline beside the amount, the sentence goes in the note underneath.
const VALUATION_BADGE_LABELS: Record<
  Exclude<ValuationState, "AVAILABLE">,
  string
> = {
  PENDING: "Pending",
  UNAVAILABLE: "Unavailable",
  MANUAL_ACTION_REQUIRED: "Needs correction",
};

/** A status that has something to flag: present, and not AVAILABLE. */
interface FlaggedValuation extends ValuationStatus {
  state: Exclude<ValuationState, "AVAILABLE">;
}

// Type predicate, not a boolean helper, so the narrowed state can index the two maps below.
const isFlaggedValuation = (
  valuation?: ValuationStatus | null,
): valuation is FlaggedValuation => !isAvailable(valuation);

// Severity by recovery path: self-resolving is informational, market-data-blocked is a warning, owner-actionable is an error.
const VALUATION_BADGE_VARIANTS: Record<
  Exclude<ValuationState, "AVAILABLE">,
  BadgeVariant
> = {
  PENDING: "info",
  UNAVAILABLE: "warning",
  MANUAL_ACTION_REQUIRED: "error",
};

/** One object drives badge text, note, and accessible name so a state reads the same visually and to a screen reader (convention documented on `AccountCard`). `null` when there's nothing to flag. */
export const valuationBadgeFor = (
  valuation?: ValuationStatus | null,
): ValuationBadge | null => {
  if (!isFlaggedValuation(valuation)) {
    return null;
  }
  return {
    label: VALUATION_BADGE_LABELS[valuation.state],
    variant: VALUATION_BADGE_VARIANTS[valuation.state],
    message: valuationMessageFor(valuation),
  };
};

/** Fetch time and market-close date are different questions — a quote fetched this morning can still belong to Friday's close — so both are tracked though every producer today sends them together or not at all. */
export const priceUpdatedFor = ({
  providerMarketDate,
  quoteFetchedAt,
}: PriceProvenance): PriceUpdated | null => {
  if (!providerMarketDate && !quoteFetchedAt) {
    return null;
  }
  const checkedAt = quoteFetchedAt
    ? formatDate(quoteFetchedAt, DateFormat.DMY_SPACE)
    : null;
  const marketDate = providerMarketDate
    ? formatDate(providerMarketDate, DateFormat.DMY_SPACE)
    : null;
  return {
    text: checkedAt
      ? `Price last updated ${checkedAt}`
      : `Price as of ${marketDate}`,
    title:
      checkedAt && marketDate ? `Market price as of ${marketDate}` : undefined,
  };
};

/** The single "is there a number on screen for this" test, so no surface decides it differently from `valuationAmountText`. */
export const isPricedValuation = ({
  value,
  valuation,
}: ValuationAmount): boolean => value !== null && isAvailable(valuation);

// Nullness alone can't decide the text: an inconsistent AVAILABLE-with-no-value gets "Unavailable" not a dash, and a value arriving alongside a non-AVAILABLE state loses to the state, since printing it would contradict the badge beside it.
export const valuationAmountText = ({
  value,
  valuation,
  format = formatCurrency,
}: ValuationAmount): string => {
  if (value !== null && isAvailable(valuation)) {
    return format(value);
  }
  if (valuation?.state === "PENDING") {
    return VALUATION_PENDING_PLACEHOLDER;
  }
  return VALUATION_PLACEHOLDER;
};

// The single definition of the PENDING test, so no screen re-types the raw state string.
export const isPendingValuation = (
  valuation?: ValuationStatus | null,
): boolean => valuation?.state === "PENDING";

// The only reasons a private price actually repairs. HISTORY_IMPORT_PENDING is included despite normally self-clearing: a rate-limited import can sit for a long time, and a private price fixes the coverage gap underneath it today.
export const isOverrideRecoverable = (
  valuation?: ValuationStatus | null,
): boolean =>
  valuation?.reason === "HISTORY_COVERAGE_UNAVAILABLE" ||
  valuation?.reason === "PRE_2020_OVERRIDE_REQUIRED" ||
  valuation?.reason === "HISTORY_IMPORT_PENDING";

/** A value still on its way that nothing the user does would bring sooner, so not worth warning about. */
export const isSelfResolvingValuation = (
  valuation?: ValuationStatus | null,
): boolean =>
  isPendingValuation(valuation) && !isOverrideRecoverable(valuation);

const PRICE_PROVENANCE_LABELS: Record<PriceProvenanceKind, string> = {
  provider: "Market quote",
  sharedManual: "Shared manual price",
  userOverride: "Your price",
};

// Listed in wire order rather than merged: a mixed point must name every source that priced it.
export const priceProvenanceText = (
  kinds: PriceProvenanceKind[],
): string | null =>
  kinds.length === 0
    ? null
    : kinds.map((kind) => PRICE_PROVENANCE_LABELS[kind]).join(", ");

/** One line of a chart's unavailable-points note: every point sharing a state and reason. */
export interface UnavailablePointGroup {
  key: string;
  message: string;
  count: number;
  isRepairable: boolean;
  /** The earliest point in the group, so a repair starts where the gap does. */
  repairAsOf: string;
}

export const unavailablePointGroupsOf = (
  points: { date: string; valuation: ValuationStatus }[],
): UnavailablePointGroup[] => {
  const groups = new Map<string, UnavailablePointGroup>();
  points.forEach(({ date, valuation }) => {
    if (isAvailable(valuation)) {
      return;
    }
    const key = `${valuation.state}|${valuation.reason ?? ""}`;
    const asOf = valuation.asOf ?? date;
    const group = groups.get(key);
    if (group) {
      group.count += 1;
      group.repairAsOf = asOf < group.repairAsOf ? asOf : group.repairAsOf;
      return;
    }
    groups.set(key, {
      key,
      message: valuationMessageFor(valuation),
      count: 1,
      isRepairable: isOverrideRecoverable(valuation),
      repairAsOf: asOf,
    });
  });
  return [...groups.values()];
};

export const unavailablePointsText = ({
  count,
  message,
}: Pick<UnavailablePointGroup, "count" | "message">): string =>
  `${count} ${count === 1 ? "date" : "dates"}: ${message}`;

// Mirrors api/src/Api/Domain/Returns/entities.ts exhaustively, so an unrecognized reason is a type error.
export type ReturnStateReason =
  | "NO_INVESTMENT_ACCOUNTS"
  | "BASIS_REPLAY_BLOCKED"
  | "VALUATION_UNAVAILABLE"
  | "CASH_BALANCE_INCOMPLETE"
  | "ZERO_LENGTH_PERIOD"
  | "NON_POSITIVE_WEIGHTED_CAPITAL"
  | "INSUFFICIENT_CASH_FLOWS"
  | "UNSOLVABLE_CASH_FLOWS";

const IRR_FLOWS_CLAUSE =
  "accounting for the size and timing of each contribution and withdrawal.";

export const IRR_DEFINITION = `Internal rate of return: your annualized return, ${IRR_FLOWS_CLAUSE}`;

export const UNANNUALIZED_IRR_DEFINITION = `Internal rate of return, ${IRR_FLOWS_CLAUSE}`;

export const PRE_TAX_GAIN_TOOLTIP = "Before income tax.";

export const PRE_TAX_IRR_TOOLTIP = `${IRR_DEFINITION} ${PRE_TAX_GAIN_TOOLTIP}`;

/** A server-owned return figure: a number, or null where the server withheld it. */
export interface ReturnPercent {
  value: number | null;
  /** Defaults to `formatSignedPercent`; pass a surface's own formatter for an unsigned figure. */
  format?: (value: number) => string;
}

// A full Record, not Partial: an unhandled reason is a compile error rather than a silent dash.
const RETURN_REASON_MESSAGES: Record<ReturnStateReason, string> = {
  NO_INVESTMENT_ACCOUNTS: "No investment accounts to measure a return over",
  BASIS_REPLAY_BLOCKED:
    "A correction is needed before this return can be measured",
  VALUATION_UNAVAILABLE:
    "A complete valuation is required to measure this return",
  CASH_BALANCE_INCOMPLETE:
    "A complete cash balance is required to measure this return",
  ZERO_LENGTH_PERIOD: "This period is too short to measure a return over",
  NON_POSITIVE_WEIGHTED_CAPITAL: "No capital was at risk over this period",
  INSUFFICIENT_CASH_FLOWS: "Not enough cash flows to measure this return",
  UNSOLVABLE_CASH_FLOWS: "These cash flows have no solvable return",
};

/** The one visible explanation for a withheld return; generic where the API named no reason. */
export const returnMessageFor = (
  stateReason?: ReturnStateReason | null,
): string =>
  stateReason
    ? RETURN_REASON_MESSAGES[stateReason]
    : "Return unavailable for this period";

// Sibling of `valuationAmountText`, not a reuse of it: a return is withheld by its own reason codes, so keying on `ValuationState` would render "Unavailable" for every one of them.
export const returnPercentText = ({
  value,
  format = formatSignedPercent,
}: ReturnPercent): string =>
  value === null ? VALUATION_PENDING_PLACEHOLDER : format(value);
